import { create } from 'zustand';

import type { AuthUser, UserRole } from '@/types/auth';
import { parseJwtPayload } from '@/lib/auth';

interface AuthStore {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginModalOpen: boolean;

  setAuth: (token: string) => void;
  clearAuth: () => void;
  setLoginModalOpen: (open: boolean) => void;
  setLoading: (loading: boolean) => void;
  hasRole: (...roles: UserRole[]) => boolean;
}

/**
 * The newest access token, shared across tabs (sessionStorage is per tab).
 * Lets a tab that needs a token reuse one another tab just obtained instead of
 * rotating the shared refresh cookie again. See lib/authRefresh.ts.
 */
export const SHARED_TOKEN_KEY = 'kp_at_shared';

function writeShared(token: string | null) {
  try {
    if (token) localStorage.setItem(SHARED_TOKEN_KEY, token);
    else localStorage.removeItem(SHARED_TOKEN_KEY);
  } catch {
    /* storage unavailable (private mode): tabs just refresh independently */
  }
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: true, // true until initial refresh attempt completes
  loginModalOpen: false,

  setAuth: (token: string) => {
    const payload = parseJwtPayload(token);
    if (!payload) return;

    const user: AuthUser = {
      id: payload.sub,
      username: payload.username,
      email: payload.email,
      role: payload.role,
      orgId: payload.org_id,
      avatarUrl: payload.avatar_url,
    };

    sessionStorage.setItem('kp_at', token);
    writeShared(token);
    set({ user, accessToken: token, isAuthenticated: true, isLoading: false });
  },

  clearAuth: () => {
    sessionStorage.removeItem('kp_at');
    writeShared(null);
    set({ user: null, accessToken: null, isAuthenticated: false, isLoading: false });
  },

  setLoginModalOpen: (open) => set({ loginModalOpen: open }),
  setLoading: (loading) => set({ isLoading: loading }),

  hasRole: (...roles) => {
    const { user } = get();
    return user != null && roles.includes(user.role);
  },
}));
